/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */

/**
 * A React functional component that renders a category management component.
 * It utilizes various React hooks and Redux for state management.
 * It also imports the necessary dependencies and JSON data for the component.
 * @returns The rendered category management component.
 */
import { debounce } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useWindowDimension from "../../components/hooks/useWindowDimension";
import {
  categoryListResponce,
  createCategoryResponce,
  deleteCategoryResponce,
  updateCategoryResponce,
} from "../../store/features/category/categorySlice";
import { showToast } from "../../store/features/toast/toastSlice";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import CategoryManagementComponent from "./categoryManagementComponent";

/**
 * A container component for managing categories.
 * @param {Object} props - The component props.
 * @returns {JSX.Element} - The rendered component.
 */
const CategoryManagementContainer = (props) => {
  /**
   * Initializes various state variables and hooks for the component.
   * @returns None
   */
  const { t } = useTranslation();
  const smarttableref = useRef();
  const categoryList = useSelector(
    (state) => state?.category?.categoryResponce
  );
  const dispatch = useDispatch();
  const dimensions = useWindowDimension();
  const pageSizeRef = useRef(15);
  const tableContainerRef = useRef(null);
  const [filteredEventData, setFilteredEventData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalName, setModalName] = useState({ modal: "add" });
  const [showUpdateCategoryModal, setUpdateCategoryModal] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [breadcrumbHeight, setBreadcrumbHeight] = useState("");
  const [showDeleteUserModal, setshowDeleteUserModal] = useState(false);
  const [updatecategoryValues, setUpdatecategoryValues] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [categoryId, setCategoryId] = useState("");

  const [loaderHtml, setLoaderHtmlHeight] = useState("");
  const [tabHeaderHeight, setTabHeaderHeight] = useState("");

  const [createValues, setCreateValues] = useState("");
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [
    iscreateCategoryModelLoaderButton,
    setIscreateCategoryModelLoaderButton,
  ] = useState(false);
  const [iscreateCategoryLoaderButton, setIscreateCategoryLoaderButton] =
    useState(false);
  const [
    isUpdateCategoryModelLoaderButton,
    setIsUpdateCategoryModelLoaderButton,
  ] = useState(false);
  const [categoryValue, setCategoryValue] = useState("");
  const categoryCount = categoryList?.length;
  const [categorytextcount, setCategorytextcount] = useState(
    modalName === "add" ? 0 : categoryValue?.length
  );
  const [placeHolder, setPlaceHolder] = useState(true);

  /**
   * Closes the delete user modal by setting the `showDeleteUserModal` state to `false`.
   * @returns None
   */
  const closeDeleteUserModal = () => setshowDeleteUserModal(false);
  const closeDeactivateModal = () => setShowStatusModal(false);

  /**
   * Opens the delete user modal and sets the state to show the modal.
   * @param {Event} e - The event object.
   * @param {Object} item - The item to be deleted.
   * @returns None
   */
  const openDeleteUserModal = (e, item) => {
    setCategoryId(item?._id);
    setshowDeleteUserModal(true);
  };

  useEffect(() => {
    if (Array.isArray(categoryList)) {
      const lowercaseSearchValue = searchValue?.toLowerCase();

      const filteredData = categoryList?.filter(
        (item) =>
          item?.title?.toLowerCase()?.includes(lowercaseSearchValue) ||
          item?.eventcount?.toString()?.includes(lowercaseSearchValue)
      );

      setFilteredEventData(filteredData);
    }
  }, [searchValue, categoryList]);

  /**
   * Updates the search value based on the filter text entered by the user.
   * @param {{string}} filterText - The filter text entered by the user.
   * @returns None
   */
  const onTableFilterChange = (filterText) => {
    setSearchValue(filterText);
  };

  /**
   * Clears the search filter by programmatically clicking the clear button.
   * @returns None
   */
  const searchFilterClear = () => {
    smarttableref.current.querySelectorAll("button")[1].click();
  };

  /**
   * Clears the search input by programmatically clicking the clear search button.
   * @returns None
   */
  const ClearSearchButton = () => {
    smarttableref.current.querySelectorAll("button")[0].click();
  };
  useEffect(() => {
    getList();
  }, []);

  /**
   * Retrieves a list of all categories from the server.
   * @returns None
   */
  const getList = async () => {
    dispatch(categoryListResponce()).then((resultAction) => {
      if (categoryListResponce.fulfilled.match(resultAction)) {
        setPlaceHolder(false);
      } else if (categoryListResponce.rejected.match(resultAction)) {
        setPlaceHolder(false);
      }
    });
  };

  const columns = [
    {
      label: (
        <>
          {t("CATEGORIES")}{" "}
          <span className="thCount">
            {" "}
            {searchValue === null ? categoryCount : filteredEventData?.length}
          </span>
        </>
      ),
      key: "title",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("No_OF_EVENTS")}</>,
      key: "eventcount",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("ACTIONS")}</>,
      key: "status",
      filter: false,
      sorting: false,
    },
  ];

  /**
   * Deletes a user by sending a PUT request to the '/categories/deleteCategories' endpoint.
   * @returns None
   * @throws {Error} If there is an error during the deletion process.
   */
  const onDeleteUser = async () => {
    setDeleteLoader(true);
    dispatch(deleteCategoryResponce(categoryId)).then((resultAction) => {
      if (deleteCategoryResponce.fulfilled.match(resultAction)) {
        dispatch(
          showToast({
            show: false,
            message: resultAction?.payload?.message,
            type: "success",
          })
        );
        closeDeleteUserModal();
        setDeleteLoader(false);
      } else if (deleteCategoryResponce.rejected.match(resultAction)) {
        closeDeleteUserModal();
        setDeleteLoader(false);
        dispatch(
          showToast({
            show: false,
            message: resultAction?.error?.message,
            type: "error",
          })
        );
      }
    });
  };

  /**
   * Opens the create category modal.
   * @returns None
   */
  const openCreateCategoryModal = () => {
    setModalName({ modal: "add" });
    setIscreateCategoryLoaderButton(true);
    setTimeout(() => {
      setShowCreateCategoryModal(true);
      setIscreateCategoryLoaderButton(false);
    }, 1000);
  };

  /**
   * Creates a category function based on the provided form data.
   * @param {{object}} form - The form data used to create the category function.
   * @returns None
   */
  const createCategoryFunction = (form) => {
    setCreateValues(form);
  };

  /**
   * Closes the create category modal and resets the category text count.
   * @returns None
   */
  const closeCreateCategoryModal = () => {
    setShowCreateCategoryModal(false);
    setCategorytextcount(0);
  };

  /**
   * Clears the update data by resetting the category value to an empty string.
   * @returns None
   */
  const ClearUpdateData = () => {
    updatecategoryValues.reset({
      category: "",
    });
  };

  /**
   * Loads more items when triggered. It sets the loading state to true, simulates a delay of 2 seconds,
   * and then sets the loading state to false and increases the page size by 2.
   * @returns None
   */

  const loadMoreItems = () => {
    if (loading) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      pageSizeRef.current += 10;
    }, 2000);
  };

  /**
   * Debounces the handleTableScroll function to prevent it from being called too frequently.
   * When the table container is scrolled, it checks if the scroll position is at the bottom
   * and if there are more items to load. If both conditions are met, it calls the loadMoreItems function.
   * @returns None
   */
  const handleTableScroll = debounce(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const isAtBottom =
        tableContainer.scrollTop + tableContainer.clientHeight >=
        tableContainer.scrollHeight;
      if (isAtBottom && categoryList?.length > pageSizeRef.current) {
        loadMoreItems();
      }
    }
  }, 200);

  /**
   * Adds an event listener to the table container element to handle scroll events.
   * The event listener is removed when the component is unmounted.
   * @param {{function}} handleTableScroll - The function to handle the table scroll event.
   * @returns None
   */
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleTableScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [handleTableScroll]);
  const pageTitle = [
    {
      title: t("EVENTS"),
      path: "/events-list",
    },
  ];
  /**
   * useEffect hook that sets the heights of various elements based on their offsetHeight
   * values. This hook is triggered when the 'loading' state changes.
   * @param {{boolean}} loading - The loading state of the component.
   * @returns None
   */
  useEffect(() => {
    loading &&
      setLoaderHtmlHeight(
        document.getElementsByClassName("table-responsive")[0].offsetHeight
      );
    setBreadcrumbHeight(
      document.getElementsByClassName("page-title-box")[0].offsetHeight
    );
    setTabHeaderHeight(document.getElementsByTagName("thead")[0].offsetHeight);
  }, [loading]);

  /**
   * Handles the submission of the create form.
   * @param {{any}} data - The data from the form submission.
   * @returns None
   */
  const handleSubmitCreate = async (data) => {
    setCategorytextcount(0);

    if (modalName?.modal === "add") {
      setCategorytextcount(0);
      setIscreateCategoryModelLoaderButton(true);
      dispatch(createCategoryResponce(data)).then((resultAction) => {
        if (createCategoryResponce.fulfilled.match(resultAction)) {
          getList();
          dispatch(
            showToast({
              show: false,
              message: resultAction?.payload?.message,
              type: "success",
            })
          );
          setShowCreateCategoryModal(false);
          setIscreateCategoryModelLoaderButton(false);
        } else if (createCategoryResponce.rejected.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message: resultAction?.error?.message,
              type: "error",
            })
          );
          setShowCreateCategoryModal(false);
          setIscreateCategoryModelLoaderButton(false);
        }
      });
    } else {
      setIsUpdateCategoryModelLoaderButton(true);
      const params = {
        title: data?.categoryname,
        id: categoryId,
      };
      dispatch(updateCategoryResponce(params)).then((resultAction) => {
        if (updateCategoryResponce.fulfilled.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message: resultAction?.payload?.message,
              type: "success",
            })
          );
          setShowCreateCategoryModal(false);
          setIsUpdateCategoryModelLoaderButton(false);
        } else if (updateCategoryResponce.rejected.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message: resultAction?.error?.message,
              type: "error",
            })
          );
          setShowCreateCategoryModal(false);
          setIsUpdateCategoryModelLoaderButton(false);
        }
      });
    }
  };

  /**
   * Closes the update category modal by setting the state of `updateCategoryModal` to `false`.
   * @returns None
   */
  const closeUpdateCategoryModal = () => {
    setUpdateCategoryModal(false);
  };

  /**
   * Updates the category values with the provided form data.
   * @param {{object}} form - The form data containing the updated category values.
   * @returns None
   */
  const updateCategoryFunction = (form) => {
    setUpdatecategoryValues(form);
  };

  /**
   * Clears the update data for a category.
   * @returns None
   */
  const clearUpdateData = () => {
    updatecategoryValues.reset({
      category: "",
    });
  };

  /**
   * Executes a side effect when the dependencies change. In this case, it updates the value of a form field
   * with the provided category value.
   * @param {{Function}} updatecategoryValues - The function to update the category values.
   * @param {{any}} categoryValue - The new value for the category.
   * @returns None
   */
  useEffect(() => {
    if (updatecategoryValues !== null) {
      updatecategoryValues.setValue("categoryname", categoryValue, {
        shouldValidate: true,
      });
    }
  }, [updatecategoryValues, categoryValue, categorytextcount]);

  /**
   * Handles the update of a category when an event is triggered.
   * @param {Event} e - The event object.
   * @param {Object} item - The category item to be updated.
   * @returns None
   */
  const onUpdateCategory = (e, item) => {
    setModalName({ modal: "Edit" });
    setCategoryValue(item?.title);
    setCategorytextcount(item?.title?.length);
    setCategoryId(item?._id);
    setTimeout(() => {
      setShowCreateCategoryModal(true);
    }, 1000);
  };

  /**
   * Constructs the Cloudinary URL using the environment variables for the Cloudinary URL,
   * name, and subfolder.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();

  const handleCategorytextChange = (e) => {
    const value = e.target.value;
    setCategorytextcount(value.length);
  };
  return (
    <CategoryManagementComponent
      createCategoryFunction={createCategoryFunction}
      clearUpdateData={clearUpdateData}
      modalName={modalName}
      showUpdateCategoryModal={showUpdateCategoryModal}
      createCategoryLoaderButton={iscreateCategoryLoaderButton}
      createCategoryModelLoaderButton={iscreateCategoryModelLoaderButton}
      updateCategoryModelLoaderButton={isUpdateCategoryModelLoaderButton}
      showCreateCategoryModal={showCreateCategoryModal}
      openCreateCategoryModal={openCreateCategoryModal}
      BreadcrumbmainTitle={true}
      pageTitle={pageTitle}
      showDeleteUserModal={showDeleteUserModal}
      openDeleteUserModal={(e, item) => {
        openDeleteUserModal(e, item);
      }}
      smarttableref={smarttableref}
      closeDeleteUserModal={() => {
        setshowDeleteUserModal(false);
      }}
      onDeleteUser={onDeleteUser}
      columns={columns}
      categoryData={categoryList}
      categorytextcount={categorytextcount}
      ClearSearchButton={ClearSearchButton}
      showStatusModal={showStatusModal}
      NoDataFound={t("NO_DATA_FOUND")}
      NoDataTitle={t("NO_DATA_FOUND")}
      NoDataDesc={t(
        "YOUR_SEARCH_DID_NOT_MATCH_ANY_CATEGORY_NAMES_PLEASE_TRY_SEARCHING_AGAIN_OR_ADD_CATEGORY"
      )}
      noDataDescTwo={t("PLEASE_TRY_SEARCHING_AGAIN_OR_ADD_CATEGORY")}
      NoDataAddButtonTitle={t("ADD_CATEGORY")}
      closeDeactiveModal={closeDeactivateModal}
      onTableFilterChange={onTableFilterChange}
      loaderHtml={loaderHtml}
      closeCreateCategoryModal={closeCreateCategoryModal}
      closeUpdateCategoryModal={closeUpdateCategoryModal}
      EmptyDataAddButton={openCreateCategoryModal}
      placeHolderShimer={placeHolder}
      itemsPerPage={pageSizeRef.current}
      ClearUpdateData={ClearUpdateData}
      searchFilterClear={searchFilterClear}
      dimensions={dimensions}
      tabHeaderHeight={tabHeaderHeight}
      breadcrumbHeight={breadcrumbHeight}
      searchValue={searchValue}
      updateCategoryFunction={(form) => {
        updateCategoryFunction(form);
      }}
      loading={loading}
      onUpdateCategory={(e, item) => onUpdateCategory(e, item)}
      handleCategorytextChange={handleCategorytextChange}
      handleSubmitCreate={handleSubmitCreate}
      categoryValue={categoryValue}
      tableContainerRef={tableContainerRef}
      emptyDataDesc={t(
        "STILL_NO_CATEGORY_HAS_BEEN_ADDED_PLEASE_CLICK_ON_THE_BELOW_BUTTON_TO_ADD_CATEGORY"
      )}
      emptyTitle={t("NO_CATEGORIES_ADDED_YET")}
      emptyImg={CLOUDINARY_URL + "v7vqwdvpm1sxnpfxov7g.png"}
      deleteLoader={deleteLoader}
      emptyDataDescTow={t("PLEASE_CLICK_ON_THE_BELOW_BUTTON_TO_ADD_CATEGORY")}
    />
  );
};

export default CategoryManagementContainer;
